/**
 * Voorbeeldplaatjes voor de tijdbalk: Plex maakt ze als "BIF"-bestand (Roku-formaat)
 * als "Video preview thumbnails" aanstaat op de server. Eén bestand met alle JPEG's achter elkaar.
 */

export interface Bif {
  /** Seconden tussen twee plaatjes */
  interval: number;
  count: number;
  /** Plaatje (object-URL) voor tijdstip t in seconden */
  frameAt(t: number): string | null;
}

const MAGIC = [0x89, 0x42, 0x49, 0x46, 0x0d, 0x0a, 0x1a, 0x0a];
const cache = new Map<string, Promise<Bif | null>>();

export function loadBif(url: string): Promise<Bif | null> {
  let p = cache.get(url);
  if (!p) {
    p = fetch(url).then(async (res) => {
      if (!res.ok) return null;
      return parseBif(await res.arrayBuffer());
    }).catch(() => null);
    cache.set(url, p);
    // Niet eindeloos bewaren: hooguit een paar titels
    if (cache.size > 6) {
      const first = cache.keys().next().value;
      if (first !== undefined) cache.delete(first);
    }
  }
  return p;
}

function parseBif(buf: ArrayBuffer): Bif | null {
  const bytes = new Uint8Array(buf);
  if (bytes.length < 64 || MAGIC.some((b, i) => bytes[i] !== b)) return null;
  const view = new DataView(buf);
  const count = view.getUint32(12, true);
  const intervalMs = view.getUint32(16, true) || 1000;
  const offsets: number[] = [];
  const stamps: number[] = [];
  for (let i = 0; i <= count; i++) {
    const at = 64 + i * 8;
    if (at + 8 > bytes.length) break;
    stamps.push(view.getUint32(at, true));
    offsets.push(view.getUint32(at + 4, true));
  }
  const urls = new Map<number, string>();
  return {
    interval: intervalMs / 1000,
    count,
    frameAt(t: number) {
      // De tijdstempels tellen in stappen van "interval"
      let i = Math.floor((t * 1000) / intervalMs);
      i = Math.max(0, Math.min(count - 1, i));
      if (stamps[i] === 0xffffffff || offsets[i + 1] === undefined) return null;
      let url = urls.get(i);
      if (!url) {
        url = URL.createObjectURL(new Blob([bytes.subarray(offsets[i], offsets[i + 1])], { type: 'image/jpeg' }));
        urls.set(i, url);
      }
      return url;
    },
  };
}
